import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebaseConfig';
import { storeUrlForUA } from './LiveScoreView';

const DAY_NAMES = ['', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function InviteInterstitial({ inviteCode }) {
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const customSchemeURL = `bowlingtracker://invite/${inviteCode}`;
  // Android visitors get Google Play, everyone else the App Store — same
  // routing as ReferralInterstitial and the live-view download buttons.
  const storeUrl = storeUrlForUA(typeof navigator !== 'undefined' ? navigator.userAgent : '');

  useEffect(() => {
    // Try to open the app automatically
    window.location.href = customSchemeURL;

    // Fetch invite details from Firestore
    async function fetchInvite() {
      try {
        const docRef = doc(db, 'invitations', inviteCode.toUpperCase());
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
          setInvite(snapshot.data());
        } else {
          setError('not_found');
        }
      } catch (err) {
        setError('network');
      } finally {
        setLoading(false);
      }
    }
    fetchInvite();
  }, [inviteCode, customSchemeURL]);

  const isExpired = invite?.expiresAt
    ? (invite.expiresAt.seconds * 1000) < Date.now()
    : false;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: '#0a0e1a', color: '#fff', fontFamily: 'Inter, sans-serif',
      padding: '24px', textAlign: 'center',
    }}>
      <img src={process.env.PUBLIC_URL + '/favicon.png'} alt="ABT" style={{ width: 80, height: 80, borderRadius: 16, marginBottom: 24 }} />

      {loading ? (
        <p style={{ fontSize: 15, color: '#999' }}>Loading invite...</p>
      ) : error === 'not_found' ? (
        <>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Invite Not Found</h2>
          <p style={{ fontSize: 15, color: '#999', margin: '0 0 24px' }}>
            This invite link is invalid or has been removed. Ask your teammate to send a new one.
          </p>
          <a href={storeUrl} style={storeLinkStyle}>Get the App</a>
        </>
      ) : error === 'network' ? (
        <>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Couldn't Load Invite</h2>
          <p style={{ fontSize: 15, color: '#999', margin: '0 0 24px' }}>
            Check your connection and try again.
          </p>
          <a href={storeUrl} style={storeLinkStyle}>Get the App</a>
        </>
      ) : isExpired ? (
        <>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Invite Expired</h2>
          <p style={{ fontSize: 15, color: '#999', margin: '0 0 24px' }}>
            This invite has expired. Ask your teammate to send a new one.
          </p>
          <a href={storeUrl} style={storeLinkStyle}>Get the App</a>
        </>
      ) : invite ? (
        <>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px' }}>Team Invite</h2>
          <p style={{ fontSize: 15, color: '#999', margin: '0 0 16px' }}>You've been invited to join:</p>

          <div style={{
            background: '#161b2e', borderRadius: 12, padding: '16px 24px',
            margin: '0 0 24px', maxWidth: 320, width: '100%',
          }}>
            <p style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px' }}>{invite.leagueName}</p>
            <p style={{ fontSize: 15, color: '#ccc', margin: '0 0 4px' }}>Team: {invite.teamName}</p>
            <p style={{ fontSize: 14, color: '#999', margin: '0 0 4px' }}>at {invite.alleyName}</p>
            {invite.dayOfWeek >= 1 && invite.dayOfWeek <= 7 && (
              <p style={{ fontSize: 14, color: '#999', margin: '0 0 4px' }}>{DAY_NAMES[invite.dayOfWeek]}s</p>
            )}
            <p style={{ fontSize: 13, color: '#777', margin: '8px 0 0' }}>Invited by {invite.creatorDisplayName}</p>
          </div>

          <a href={customSchemeURL} style={openButtonStyle}>Open in App</a>
          <a href={storeUrl} style={storeLinkStyle}>Get the App</a>
        </>
      ) : null}
    </div>
  );
}

const openButtonStyle = {
  display: 'inline-block', padding: '14px 32px', background: '#E53935', color: '#fff',
  borderRadius: 12, fontWeight: 600, fontSize: 16, textDecoration: 'none', marginBottom: 16,
};

const storeLinkStyle = {
  display: 'inline-block', padding: '14px 32px', background: 'transparent', color: '#999',
  borderRadius: 12, fontWeight: 500, fontSize: 14, textDecoration: 'none',
  border: '1px solid #333',
};
